import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../src/contexts/AuthContext';
import {
    Loader2,
    ArrowLeft,
    ChevronDown,
    Mail,
    MessageCircle,
    BookOpen,
    ExternalLink,
    HelpCircle,
} from 'lucide-react';
import PortalConsumidorHeader from './PortalConsumidorHeader';
import { useMarcaPortal, useTituloPortal } from './marca/MarcaPortalContext';
import { linkWhatsapp } from './marca/marcaPortal';

// Portal do Consumidor — Ajuda (rota /portal-consumidor/ajuda).
// As perguntas vêm da marca do portal (marca/marcaPortal.ts): as da Vigor são as mesmas 4
// do faqItems de views/ManualConsumidorView.tsx; as da Gran (#6117) são só o que é certo
// para o cliente dela — fidelidade e multa dependem do contrato da Gran.

const PortalConsumidorAjuda = () => {
    const { user, profile, loading, signOut } = useAuth();
    const { marca } = useMarcaPortal();
    const FAQ_ITEMS = marca.faq;
    const EMAIL_SUPORTE = marca.contato.email;
    const [abertaIdx, setAbertaIdx] = useState<number | null>(0);

    useTituloPortal('Ajuda');

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <Loader2 size={32} className="animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <PortalConsumidorHeader />

            <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 space-y-6">
                <div className="flex items-center gap-3">
                    <Link
                        to="/portal-consumidor/inicio"
                        className="p-2 rounded-full text-gray-400 hover:text-primary hover:bg-gray-100 transition-colors"
                    >
                        <ArrowLeft size={20} />
                    </Link>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Ajuda</h1>
                        <p className="text-sm text-gray-500">Dúvidas frequentes sobre sua assinatura de energia.</p>
                    </div>
                </div>

                {/* FAQ */}
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                    {FAQ_ITEMS.map((item, i) => {
                        const aberta = abertaIdx === i;
                        return (
                            <div key={item.q} className={i > 0 ? 'border-t border-gray-100' : ''}>
                                <button
                                    onClick={() => setAbertaIdx(aberta ? null : i)}
                                    aria-expanded={aberta}
                                    aria-controls={`faq-painel-${i}`}
                                    className="w-full flex items-center justify-between gap-4 px-5 sm:px-6 py-4 text-left hover:bg-gray-50 transition-colors"
                                >
                                    <span className={`text-sm sm:text-base font-bold ${aberta ? 'text-primary' : 'text-gray-800'}`}>
                                        {item.q}
                                    </span>
                                    <ChevronDown
                                        size={18}
                                        aria-hidden="true"
                                        className={`shrink-0 text-gray-400 transition-transform duration-200 ${aberta ? 'rotate-180 text-primary' : ''}`}
                                    />
                                </button>
                                {aberta && (
                                    <div id={`faq-painel-${i}`} className="px-5 sm:px-6 pb-5">
                                        <p className="text-sm text-gray-600 leading-relaxed">{item.a}</p>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Manual completo — o manual existente é o da Vigor (#6117: some na Gran). */}
                {marca.mostrarManual && (
                <a
                    href="#/manual-do-consumidor"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 bg-white rounded-2xl border border-gray-100 shadow-sm p-5 hover:border-primary/30 hover:shadow-md transition-all group"
                >
                    <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                        <BookOpen size={22} className="text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="font-bold text-gray-900">Manual completo do consumidor</p>
                        <p className="text-sm text-gray-500">Entenda em detalhes como funciona sua fatura, o desconto e o modelo de assinatura.</p>
                    </div>
                    <ExternalLink size={18} className="text-gray-300 group-hover:text-primary transition-colors shrink-0" />
                </a>
                )}

                {/* Não achou? Fale com a gente */}
                <div className="bg-primary/5 border border-primary/15 rounded-2xl p-6 text-center space-y-4">
                    <HelpCircle size={28} className="text-primary mx-auto" />
                    <div>
                        <p className="font-bold text-gray-900">Não encontrou o que precisava?</p>
                        <p className="text-sm text-gray-500 mt-1">Fale direto com o nosso time de atendimento.</p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <a
                            href={linkWhatsapp(marca)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center gap-2 py-2.5 px-5 rounded-xl text-sm font-bold text-white bg-primary hover:bg-primary/90 transition-colors"
                        >
                            <MessageCircle size={16} />
                            WhatsApp
                        </a>
                        <a
                            href={`mailto:${EMAIL_SUPORTE}`}
                            className="inline-flex items-center justify-center gap-2 py-2.5 px-5 rounded-xl text-sm font-bold text-primary bg-white border border-primary/20 hover:bg-primary/5 transition-colors"
                        >
                            <Mail size={16} />
                            {EMAIL_SUPORTE}
                        </a>
                    </div>
                </div>

                {/* Cancelamento (#5833): entrada discreta, no fim da Ajuda — o
                    cliente acha quando procura, sem virar convite. */}
                <div className="text-center">
                    <Link to="/portal-consumidor/antes-de-cancelar"
                        className="text-xs font-semibold text-gray-400 hover:text-gray-600 underline underline-offset-4 transition-colors">
                        Quero cancelar minha adesão
                    </Link>
                </div>
            </main>
        </div>
    );
};

export default PortalConsumidorAjuda;